export interface DateOnlyPickerValue {
  dateObject: Date | null
  dateString: string
  timeString: string
  endTimeString: string
}

export type DateTimePickerValue = DateOnlyPickerValue

const EMPTY_DATE_ONLY_PICKER_VALUE: DateOnlyPickerValue = {
  dateObject: null,
  dateString: "",
  timeString: "",
  endTimeString: "",
}

function parseApiDateTime(raw: string): Date | null {
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T")
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return date
}

function padTwoDigits(value: number): string {
  return String(value).padStart(2, "0")
}

export function buildDateTimePickerValueFromApi(raw: unknown): DateTimePickerValue {
  if (typeof raw !== "string" || !raw.trim()) {
    return { ...EMPTY_DATE_ONLY_PICKER_VALUE }
  }

  const date = parseApiDateTime(raw.trim())
  if (!date) {
    return { ...EMPTY_DATE_ONLY_PICKER_VALUE }
  }

  const dateString = `${date.getFullYear()}-${padTwoDigits(date.getMonth() + 1)}-${padTwoDigits(date.getDate())}`
  const timeString = `${padTwoDigits(date.getHours())}:${padTwoDigits(date.getMinutes())}`

  return {
    dateObject: date,
    dateString,
    timeString,
    endTimeString: timeString,
  }
}

export function apiDateFromDateTimePickerValue(value: DateTimePickerValue | null): string {
  if (!value?.dateString) {
    return ""
  }

  const time = (value.timeString || "00:00").substring(0, 5)
  return `${value.dateString} ${time}:00`
}

const INTL_LOCALE_BY_APP_LOCALE: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  pt: "pt-PT",
  it: "it-IT",
  de: "de-DE",
}

function intlLocaleFromAppLocale(locale: string): string {
  return INTL_LOCALE_BY_APP_LOCALE[locale] ?? "fr-FR"
}

export function formatLocalizedDateTime(value: string, locale: string): string {
  if (!value) {
    return ""
  }

  const date = parseApiDateTime(value)
  if (!date) {
    return value
  }

  const intlLocale = intlLocaleFromAppLocale(locale)
  return new Intl.DateTimeFormat(intlLocale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date)
}

export function formatLocalizedDateLong(value: string, locale: string): string {
  if (!value) {
    return ""
  }

  const date = parseApiDateTime(value)
  if (!date) {
    return value
  }

  const intlLocale = intlLocaleFromAppLocale(locale)
  return new Intl.DateTimeFormat(intlLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date)
}

export function normalizeDateToYmd(raw: unknown): string {
  if (raw == null) {
    return ""
  }
  if (typeof raw !== "string" && typeof raw !== "number") {
    return ""
  }
  const s = String(raw).trim()
  if (!s) {
    return ""
  }
  const head = s.length >= 10 ? s.substring(0, 10) : s
  const normalized = head.replaceAll("/", "-")
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return normalized
  }
  return ""
}

export function buildDateOnlyPickerValue(raw: unknown): DateOnlyPickerValue {
  const ymd = normalizeDateToYmd(raw)
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return { ...EMPTY_DATE_ONLY_PICKER_VALUE }
  }
  const [y, mo, d] = ymd.split("-").map((x) => Number.parseInt(x, 10))
  if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) {
    return { ...EMPTY_DATE_ONLY_PICKER_VALUE }
  }
  const dateObject = new Date(y, mo - 1, d)
  return {
    dateObject,
    dateString: ymd,
    timeString: "",
    endTimeString: "",
  }
}

export function buildDatePickerMaxProps(maxDate: string | null | undefined): Record<string, string> {
  if (typeof maxDate === "string" && maxDate.length >= 10) {
    return { max: maxDate.substring(0, 10) }
  }
  return {}
}

export function dateStringFromPickerInput(payload: { dateString?: string } | null): string {
  return payload?.dateString ? String(payload.dateString).trim() : ""
}
